<script lang="ts">
import type { Component } from 'vue';
import type { AnyItem, CompositePrimaryKey, FieldSelection, Filter, GlobalPrimitiveFieldId, ResolvedMonospacePrimitiveField } from '#layers/data-model/public';
import type { CalendarDisplayOptions, SharedLayoutEmits, SharedLayoutProps } from '#layers/listing/types/layout';
import { Temporal } from 'temporal-polyfill';
import { getMonthRangeForDate } from '#layers/base/public';

export type ScheduleXViewId = 'month-grid' | 'week' | 'day' | 'list';

export interface LayoutCalendarProps extends SharedLayoutProps<CalendarDisplayOptions> {
	renderDisplayTemplate: Component;
}

export interface LayoutCalendarEmits extends SharedLayoutEmits {
	'open': [key: CompositePrimaryKey];
	'resetView': [];
	'update:displayOptions': [displayOptions: Record<string, unknown>];
}
</script>

<script setup lang="ts">
import type { CalendarRange } from './build-calendar-range-filter';
import {
	createCalendar,
	createViewDay,
	createViewList,
	createViewMonthGrid,
	createViewWeek,
} from '@schedule-x/calendar';
import { createCalendarControlsPlugin } from '@schedule-x/calendar-controls';
import { ScheduleXCalendar } from '@schedule-x/vue';
import { createReusableTemplate, refDebounced, whenever } from '@vueuse/core';
import { invert } from 'lodash-es';
import { computed, ref, shallowRef, toRef, watch, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplayTemplateFieldSelection, useFieldSelectionForFieldIds } from '#layers/blocks/public';
import { useItems } from '#layers/collection-query/public';
import { CollectionReads, getCollectionPrimaryKeyFields, getItemFieldValue, getItemPrimaryKey, getItemPrimaryKeyRouteString, isEmptyFilter, isPrimitiveTypeDateTime, mergeAnd, mergeFieldSelections, resolveDisplayTemplatePlainText, stringifyStableId, usePrimitiveField, useWorkspaceSchemaStore } from '#layers/data-model/public';
import { injectListingContext } from '#layers/listing/components/Listing/context';
import ListingErrorState from '#layers/listing/components/Listing/ErrorState.vue';
import { useCalendarDateFieldGate } from '#layers/listing/composables/use-calendar-date-field-gate';
import { exposeListingQueryToBlockRootContext } from '#layers/listing/composables/use-listing-query-block-root-context';
import { useSelection } from '#layers/listing/composables/use-selection';
import { WEEKDAY_SHORT_LABEL_KEYS } from '#layers/listing/constants/calendar';
import { buildCalendarRangeFilter } from './build-calendar-range-filter';
import LayoutCalendarDisplayOptions, { getLocaleFirstDayOfWeek, initializeLayoutCalendarOptions } from './LayoutCalendarDisplayOptions.vue';
import LayoutCalendarEventContent from './LayoutCalendarEventContent.vue';
import LayoutCalendarHeader from './LayoutCalendarHeader.vue';

const {
	collection: collectionProp,
	filter: filterProp,
	displayOptions: displayOptionsProp,
	selectionKeys,
	projectionKeys,
	searchTerm,
} = defineProps<LayoutCalendarProps>();
const emit = defineEmits<LayoutCalendarEmits>();

const collection = toRef(() => collectionProp);
const filter = toRef(() => filterProp);

const { setDisplayOptionsComponent } = injectListingContext();
setDisplayOptionsComponent(LayoutCalendarDisplayOptions);

const dateFieldGate = useCalendarDateFieldGate(collection);
const workspaceSchema = useWorkspaceSchemaStore();
const displayOptions = computed(() =>
	initializeLayoutCalendarOptions({ collection: collectionProp, options: displayOptionsProp, allowsStartField: dateFieldGate.allowsStartField, querySchema: workspaceSchema.querySchema }),
);

// Saved date fields the read no longer returns or filters by place no events and send no range.
const configuredStartField = usePrimitiveField(() => displayOptions.value.startDateField);
const configuredEndField = usePrimitiveField(() => displayOptions.value.endDateField);
const startField = computed(() => configuredStartField.value && dateFieldGate.allowsStartField(configuredStartField.value) ? configuredStartField.value : undefined);
const endField = computed(() => configuredEndField.value && dateFieldGate.allowsEndField(configuredEndField.value, startField.value) ? configuredEndField.value : undefined);
const titleTemplate = computed(() => displayOptions.value.titleTemplate);

// Fallback slice for the first render only. makeCalendarApp() overwrites this
// synchronously from schedule-x's real range before any fetch runs (see below).
const currentViewRange = ref<CalendarRange>(getMonthRangeForDate(displayOptions.value.currentDate));

const mergedFilter = computed<Filter>(() => {
	if (!startField.value) return filter.value;

	const rangeFilter = buildCalendarRangeFilter({ startField: startField.value, range: currentViewRange.value, endField: endField.value });
	if (!rangeFilter) return filter.value;
	if (isEmptyFilter(filter.value)) return rangeFilter;
	return mergeAnd(filter.value, rangeFilter);
});

const titleFieldSelection = useDisplayTemplateFieldSelection(collection, CollectionReads.Many, () => titleTemplate.value ?? undefined);

const dateFieldIds = computed<GlobalPrimitiveFieldId[]>(() =>
	[startField.value?.id, endField.value?.id].filter((id): id is GlobalPrimitiveFieldId => !!id),
);
const dateFieldsSelection = useFieldSelectionForFieldIds(collection, dateFieldIds);

const selection = defineModel<CompositePrimaryKey[]>('selection', { default: () => [] });
const primaryKeyFields = computed(() => getCollectionPrimaryKeyFields(collection.value));
const { selectionFields } = useSelection({
	selection,
	primaryKeyFields,
	selectionKeys: () => selectionKeys,
	projectionKeys: () => projectionKeys,
});

const querySelection = computed<FieldSelection>(() =>
	mergeFieldSelections(titleFieldSelection.value, dateFieldsSelection.value, { fields: [...primaryKeyFields.value, ...selectionFields.value].map((field) => field.apiName) }),
);

// The search input emits on every keystroke, so coalesce it into a single query
// change rather than fetching per keystroke.
const debouncedSearch = refDebounced(computed(() => searchTerm), 300);

const { items, error, refresh, query: itemsQuery } = useItems({
	collection,
	query: () => ({ filter: mergedFilter.value, ...querySelection.value, search: debouncedSearch.value }),
	pageSize: ref(500),
	// The calendar's query only changes on discrete view/range switches, so throttling
	// would just delay the fetch behind the 500ms window without coalescing anything.
	throttle: false,
});

exposeListingQueryToBlockRootContext(() => ({
	collection: collection.value,
	query: itemsQuery.value,
}));

function toCalendarTemporal(value: unknown, field: ResolvedMonospacePrimitiveField | null | undefined): Temporal.PlainDate | Temporal.ZonedDateTime | null {
	if (value == null || !field) return null;

	// RStore's field parser already resolves values to their canonical Temporal type
	// (date -> PlainDate, dateTime -> PlainDateTime, zoned -> ZonedDateTime), so just narrow.
	if (isPrimitiveTypeDateTime(field.type)) {
		if (value instanceof Temporal.ZonedDateTime) return value;
		if (value instanceof Temporal.PlainDateTime) return value.toZonedDateTime('UTC');
		return null;
	}

	return value instanceof Temporal.PlainDate ? value : null;
}

const calendarData = computed(() => {
	const keyMap = new Map<string, CompositePrimaryKey>();
	const itemMap = new Map<string, AnyItem>();
	const sf = startField.value;
	if (!sf) return { events: [], keyMap, itemMap };

	const events = items.value.flatMap((item) => {
		const start = toCalendarTemporal(getItemFieldValue(item, sf), sf);
		if (!start) return [];

		const key = getItemPrimaryKey(item, collection.value);
		const id = stringifyStableId(key);
		const end = toCalendarTemporal(getItemFieldValue(item, endField.value), endField.value) ?? start;

		keyMap.set(id, key);
		itemMap.set(id, item);
		return [{ id, title: resolveDisplayTemplatePlainText(titleTemplate.value, item, collection.value, workspaceSchema.querySchema) ?? getItemPrimaryKeyRouteString(item, collection.value), start, end }];
	});

	return { events, keyMap, itemMap };
});

// Our viewMode names differ from schedule-x's view ids (e.g. month <-> month-grid).
// Keep one map and derive the reverse with invert() so both directions stay in sync.
const VIEW_MODE_TO_SCHEDULE_X = {
	month: 'month-grid',
	week: 'week',
	day: 'day',
	list: 'list',
} as const satisfies Record<NonNullable<CalendarDisplayOptions['viewMode']>, ScheduleXViewId>;

const SCHEDULE_X_TO_VIEW_MODE = invert(VIEW_MODE_TO_SCHEDULE_X) as Record<ScheduleXViewId, NonNullable<CalendarDisplayOptions['viewMode']>>;

function toScheduleXView(viewMode: CalendarDisplayOptions['viewMode']): ScheduleXViewId {
	return VIEW_MODE_TO_SCHEDULE_X[viewMode ?? 'month'];
}

const currentView = ref<ScheduleXViewId>(toScheduleXView(displayOptions.value.viewMode));

const resolvedFirstDayOfWeek = computed(() => displayOptions.value.firstDayOfWeek ?? getLocaleFirstDayOfWeek());

const [DefineEventContent, ReuseEventContent] = createReusableTemplate<{ calendarEvent: { id: string | number; title?: string } }>();

const { t } = useI18n();

const dayNames = computed(() => {
	const labels = WEEKDAY_SHORT_LABEL_KEYS.map((key) => t(key));
	return resolvedFirstDayOfWeek.value === 1 ? [...labels.slice(1), labels[0]] : labels;
});

const calendarControls = shallowRef(createCalendarControlsPlugin());

// Last date shown — tells a real navigation from a view-only granularity change.
let lastSeenDate: Temporal.PlainDate | undefined;

function emitDisplayOptions(patch: Partial<CalendarDisplayOptions>) {
	// Spread the RAW prop, not the `displayOptions` computed. Spreading raw keeps those
	// defaults out of the persisted saved-view payload. Same pattern as Table/Cards.
	emit('update:displayOptions', { ...displayOptionsProp, ...patch });
}

function tryParseSelectedDate(value: string | undefined) {
	if (!value) return;

	try {
		return Temporal.PlainDate.from(value);
	}
	catch {}
}

// Selected date exposed as a PlainDate; only stringifies at the persisted display-options boundary.
const selectedDate = computed<Temporal.PlainDate | undefined>({
	get: () => tryParseSelectedDate(displayOptions.value.currentDate),
	set: (date) => emitDisplayOptions({ currentDate: date?.toString() }),
});

function toCalendarRange(range: { start: Temporal.ZonedDateTime; end: Temporal.ZonedDateTime }): CalendarRange {
	return { start: range.start.toPlainDate(), end: range.end.toPlainDate() };
}

function makeCalendarApp() {
	const controls = createCalendarControlsPlugin();
	calendarControls.value = controls;

	const app = createCalendar({
		views: [createViewMonthGrid(), createViewWeek(), createViewDay(), createViewList()],
		defaultView: currentView.value,
		firstDayOfWeek: resolvedFirstDayOfWeek.value === 0 ? 7 : 1,
		isResponsive: false,
		...(selectedDate.value && { selectedDate: selectedDate.value }),
		events: [],
		callbacks: {
			onRangeUpdate(range) {
				const next = toCalendarRange(range);

				if (next.start.equals(currentViewRange.value.start) && next.end.equals(currentViewRange.value.end)) return;
				currentViewRange.value = next;

				// schedule-x swallows the first emit, so this only fires on real changes.
				const date = controls.getDate();
				const selected = selectedDate.value;

				if (selected && date.equals(selected)) {
					lastSeenDate = date;
					return;
				}

				if (lastSeenDate && date.equals(lastSeenDate)) return;

				lastSeenDate = date;
				selectedDate.value = date;
			},
			onEventClick(event) {
				const key = calendarData.value.keyMap.get(String(event.id));
				if (key) emit('open', key);
			},
		},
	}, [controls]);

	// Seed lastSeenDate from the calendar's initial date.
	lastSeenDate = controls.getDate();

	// Overwrite the fallback range with schedule-x's real range for the restored view.
	const range = controls.getRange();
	if (range) currentViewRange.value = toCalendarRange(range);

	return app;
}

const calendarKey = ref(0);
const calendarApp = shallowRef(makeCalendarApp());

// Schedule-X doesn't support reactively changing firstDayOfWeek after creation,
// so we recreate the app and force the calendar to remount via calendarKey.
// flush: 'sync' ensures calendarApp is reassigned before the watchEffect below reacts.
watch(
	resolvedFirstDayOfWeek,
	() => {
		calendarApp.value = makeCalendarApp();
		calendarKey.value++;
	},
	{ flush: 'sync' },
);

// Re-seed when the persisted date hydrates or changes externally (e.g. switching saved views).
function applyExternalDate(date: Temporal.PlainDate) {
	if (lastSeenDate && date.equals(lastSeenDate)) return;
	calendarControls.value.setDate(date);
}

whenever(() => selectedDate.value, applyExternalDate);

// syncs display options panel changes
watch(
	() => displayOptions.value.viewMode,
	(viewMode) => {
		currentView.value = toScheduleXView(viewMode);
	},
);

// persist view mode changes back to display options
watch(currentView, (view) => {
	const viewMode = SCHEDULE_X_TO_VIEW_MODE[view];
	if (viewMode !== displayOptions.value.viewMode) emitDisplayOptions({ viewMode });
});

// syncs reactive event data into the calendar
watchEffect(() => {
	calendarApp.value.events.set(calendarData.value.events);
});
</script>

<template>
	<div class="flex h-full flex-col overflow-hidden">
		<div
			v-if="!startField"
			class="flex flex-1 items-center justify-center text-sm text-muted"
		>
			{{ $t('layout.calendar.no_start_date_field') }}
		</div>

		<!-- Ahead of the calendar: cached events answer whichever query last succeeded, so leaving them up would present them as the result of the one that just failed. -->
		<ListingErrorState
			v-else-if="error"
			:error
			:can-reset-view
			@retry="refresh"
			@reset-view="$emit('resetView')"
		/>

		<div
			v-else
			data-calendar
			class="flex flex-1 flex-col overflow-hidden"
		>
			<LayoutCalendarHeader
				v-model="currentView"
				:range="currentViewRange"
				:calendar-controls
			/>

			<div
				v-if="currentView === 'month-grid'"
				class="flex px-px text-dimmed"
			>
				<span
					v-for="name in dayNames"
					:key="name"
					class="flex-1 py-1 text-end text-xs uppercase"
					:style="{ paddingInlineEnd: 'var(--sx-spacing-padding2)' }"
				>
					{{ name }}
				</span>
			</div>

			<DefineEventContent v-slot="{ calendarEvent }">
				<LayoutCalendarEventContent
					:calendar-event
					:item="calendarData.itemMap.get(String(calendarEvent.id))"
					:title-template
					:collection-id="collection.id"
					:render-display-template
				/>
			</DefineEventContent>

			<ClientOnly>
				<ScheduleXCalendar
					:key="calendarKey"
					:calendar-app
				>
					<template #headerContent>
						<span />
					</template>
					<template #timeGridEvent="slotProps">
						<ReuseEventContent v-bind="slotProps" />
					</template>
					<template #dateGridEvent="slotProps">
						<ReuseEventContent v-bind="slotProps" />
					</template>
					<template #monthGridEvent="slotProps">
						<ReuseEventContent v-bind="slotProps" />
					</template>
					<template #monthAgendaEvent="slotProps">
						<ReuseEventContent v-bind="slotProps" />
					</template>
				</ScheduleXCalendar>
			</ClientOnly>
		</div>
	</div>
</template>

<style scoped>
/* Some custom css is done here to give the calendar a more Monospace feel */
[data-calendar] {
	/* Primary */
	--sx-color-primary: var(--color-primary-500);
	--sx-color-on-primary: #fff;
	--sx-color-primary-container: var(--color-primary-100);
	--sx-color-on-primary-container: var(--color-primary-900);

	/* Surface & Background */
	--sx-color-surface: var(--ui-bg);
	--sx-color-surface-dim: var(--ui-bg-muted);
	--sx-color-surface-bright: var(--ui-bg-elevated);
	--sx-color-on-surface: var(--ui-text);
	--sx-color-surface-container: var(--ui-bg);
	--sx-color-surface-container-low: var(--ui-bg-muted);
	--sx-color-surface-container-high: var(--ui-bg-elevated);
	--sx-color-background: var(--ui-bg);
	--sx-color-on-background: var(--ui-text);

	/* Borders */
	--sx-color-outline: var(--ui-border-muted);
	--sx-color-outline-variant: var(--ui-border-muted);
	--sx-border: 1px solid var(--ui-border-muted);

	/* Neutral / Text */
	--sx-color-neutral: var(--ui-text);
	--sx-color-neutral-variant: var(--ui-text-dimmed);

	/* Shadow & tint */
	--sx-color-shadow: transparent;
	--sx-color-surface-tint: var(--color-primary-500);

	/* Internal overrides */
	--sx-internal-color-gray-ripple-background: var(--ui-bg-muted);
	--sx-internal-color-light-gray: var(--ui-bg-muted);
	--sx-internal-color-text: var(--ui-text);
}

/* Own the space left after the header and scroll internally, instead of
   height:100% (which sizes to the full container and overruns the header). */
[data-calendar] :deep(.sx-vue-calendar-wrapper) {
	flex: 1;
	min-height: 0;
	font-family: inherit;
}

[data-calendar] :deep(.sx__calendar) {
	border: none;
	border-radius: 0;
}

[data-calendar] :deep(.sx__calendar-header) {
	display: none;
}

/* Remove Material Design ripple effect */
[data-calendar] :deep(.sx__ripple::before),
[data-calendar] :deep(.sx__ripple::after) {
	all: unset !important;
}

/* Hide built-in day names — we render our own row above the calendar */
[data-calendar] :deep(.sx__month-grid-day__header-day-name) {
	display: none;
}

/* Right-align day headers in month grid */
[data-calendar] :deep(.sx__month-grid-day__header) {
	align-items: flex-end;
	padding-inline-end: var(--sx-spacing-padding2);
}

/* Square "today" indicator instead of circle */
[data-calendar] :deep(.sx__month-grid-day__header-date),
[data-calendar] :deep(.sx__week-grid__date--is-today .sx__week-grid__date-number) {
	border-radius: var(--ui-radius);
}

/* Event modal */
[data-calendar] :deep(.sx__event-modal.sx__event-modal-default) {
	border-radius: 0.5rem;
	box-shadow: none;
	border: 1px solid var(--ui-border);
}

/* Zero-duration timed events (same start/end) get height:0% from schedule-x and
   vanish in the week/day time grids. Guarantee a minimum height so they read as a
   thin point-in-time marker, without inventing a duration in the data. */
[data-calendar] :deep(.sx__time-grid-event) {
	min-height: 1.25rem;
}

/* Disable Schedule-X's month slide animation */
[data-calendar] :deep(.sx__slide-left),
[data-calendar] :deep(.sx__slide-right) {
	animation: none;
}
</style>
